import { useEffect, useRef } from 'react';
import type { Board as BoardState, Colour, PieceType } from '@rookandroll/engine';
import { squareName } from '@rookandroll/engine';

const GLYPH: Record<PieceType, string> = { K: '♚', Q: '♛', R: '♜', B: '♝', N: '♞', P: '♟' };
const SQ = 9;
const OFF = 6;
const FONT = '"Noto Sans Symbols 2","Segoe UI Symbol","DejaVu Sans",sans-serif';

/**
 * Top-left corner of square `i` on the drawn board. When `flipped` the whole
 * board is rotated 180 degrees, so the player's own pieces stay at the bottom.
 */
export function squarePosition(i: number, flipped: boolean): { x: number; y: number } {
  const r = (i / 8) | 0;
  const f = i % 8;
  return { x: OFF + (flipped ? 7 - f : f) * SQ, y: OFF / 2 + (flipped ? 7 - r : r) * SQ };
}

interface Props {
  board: BoardState;
  /** Squares holding a piece the player can move now. */
  movable: Set<number>;
  /** Squares the selected piece (or an added pawn) may go to. */
  targets: Set<number>;
  selected: number | null;
  last: { from: number | null; to: number } | null;
  onSquare: (i: number) => void;
  /** Rotate the board 180 degrees (the player controls the dark pieces). */
  flipped?: boolean;
}

export function Board({ board, movable, targets, selected, last, onSquare, flipped = false }: Props) {
  // turn the board with an animation only after the first draw
  const drawn = useRef(false);
  const animate = drawn.current;
  useEffect(() => {
    drawn.current = true;
  });
  const squares = [];
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      const i = r * 8 + f;
      const { x, y } = squarePosition(i, flipped);
      const actionable = targets.has(i) || movable.has(i);
      const name = squareName(i);
      squares.push(
        <g key={i}>
          <rect
            x={x}
            y={y}
            width={SQ}
            height={SQ}
            fill={(r + f) % 2 ? 'var(--sq-d)' : 'var(--sq-l)'}
            style={actionable ? { cursor: 'pointer' } : undefined}
            tabIndex={actionable ? 0 : undefined}
            role={actionable ? 'button' : undefined}
            aria-label={actionable ? `Square ${name}${targets.has(i) ? ', move here' : ', select piece'}` : undefined}
            onClick={() => onSquare(i)}
            onKeyDown={(e) => {
              if (actionable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onSquare(i);
              }
            }}
          />
          {last && (last.to === i || last.from === i) && (
            <rect x={x + 0.4} y={y + 0.4} width={SQ - 0.8} height={SQ - 0.8} fill="none" stroke="var(--yellow)" strokeWidth=".6" pointerEvents="none" />
          )}
          {selected === i && (
            <rect x={x + 0.4} y={y + 0.4} width={SQ - 0.8} height={SQ - 0.8} fill="none" stroke="var(--sel)" strokeWidth=".8" pointerEvents="none" />
          )}
        </g>,
      );
    }
  }
  const mono = '"JetBrains Mono",monospace';
  return (
    <svg key={flipped ? 'flipped' : 'upright'} className={`board${flipped ? ' flip' : ''}${animate ? ' turn' : ''}`} viewBox="0 0 80 80" role="group" aria-label="Chessboard">
      {squares}
      {Array.from({ length: 8 }, (_, f) => (
        <text key={`f${f}`} x={OFF + (flipped ? 7 - f : f) * SQ + SQ / 2} y={OFF / 2 + 8 * SQ + 3.6} textAnchor="middle" fontSize="2.4" fill="var(--muted)" fontFamily={mono}>
          {'abcdefgh'[f]}
        </text>
      ))}
      {Array.from({ length: 8 }, (_, r) => (
        <text key={`r${r}`} x={OFF - 1.8} y={OFF / 2 + (flipped ? 7 - r : r) * SQ + SQ / 2 + 0.9} textAnchor="end" fontSize="2.4" fill="var(--muted)" fontFamily={mono}>
          {8 - r}
        </text>
      ))}
      {board.map((p, i) =>
        p ? (
          <text
            key={`p${i}`}
            x={squarePosition(i, flipped).x + SQ / 2}
            y={squarePosition(i, flipped).y + SQ / 2 + SQ * 0.34}
            textAnchor="middle"
            fontSize={SQ * 0.82}
            fontFamily={FONT}
            fill={p.c === 'l' ? 'var(--pl-fill)' : 'var(--pd-fill)'}
            stroke={p.c === 'l' ? 'var(--pl-stroke)' : 'var(--pd-stroke)'}
            strokeWidth={SQ * 0.045}
            paintOrder="stroke"
            pointerEvents="none"
          >
            {GLYPH[p.t] + '︎'}
          </text>
        ) : null,
      )}
      {board.map((p, i) => {
        if (!targets.has(i)) return null;
        const cx = squarePosition(i, flipped).x + SQ / 2;
        const cy = squarePosition(i, flipped).y + SQ / 2;
        return p ? (
          <circle key={`t${i}`} cx={cx} cy={cy} r={SQ * 0.44} fill="none" stroke="var(--hl)" strokeWidth=".9" pointerEvents="none" />
        ) : (
          <circle key={`t${i}`} cx={cx} cy={cy} r="1.5" fill="var(--hl)" pointerEvents="none" />
        );
      })}
    </svg>
  );
}

export type { Colour };
